import { CheckCircle, Hourglass, WarningCircle } from "@phosphor-icons/react";
import { type ReactNode } from "react";

/* The wizard's traffic status box, shared by both Verify flows. Three states, because
 * giving up needs to say so: hiding the box on timeout left a gap and no next move. */
export function AgentTrafficCard({
  used,
  stalled = false,
  waitingTitle = "Waiting for your agent's first request…",
  hint,
  action,
}: {
  used: boolean;
  /** Waiting has run out. Ignored when `used` — traffic outranks a spent budget. */
  stalled?: boolean;
  /** The waiting headline. Say what the reader still has to do, not what we are doing —
   *  nothing here runs until they act. */
  waitingTitle?: string;
  /** Omit where the headline says it all; a second line the reader doesn't need reads as
   *  explaining the box rather than helping them. */
  hint?: ReactNode;
  /** Offered inside the box once stalled, where the indicator used to be. */
  action?: ReactNode;
}) {
  const givenUp = stalled && !used;
  const waiting = !used && !givenUp;
  const { background, borderColor, color, Icon, title } = used
    ? {
        background: "var(--tint-green)",
        borderColor: "#75B999",
        color: "var(--val-green)",
        Icon: CheckCircle,
        title: "We got your request!",
      }
    : givenUp
      ? {
          background: "var(--tint-amber)",
          borderColor: "var(--val-amber)",
          color: "var(--val-amber)",
          Icon: WarningCircle,
          title: "No request yet",
        }
      : {
          background: "#fff7f8",
          borderColor: "#fee2e2",
          color: "var(--accent-deep)",
          Icon: Hourglass,
          title: waitingTitle,
        };

  return (
    <div
      aria-live="polite"
      className="flex flex-col gap-2 rounded-[5px] border p-4"
      style={{ background, borderColor }}
    >
      <div className="flex items-center gap-2">
        {/* Regular weight while waiting: the settled states are conclusions and earn a
            filled glyph, this one is the quiet state. The glyph itself never moves —
            only the headline breathes, since a spinning icon was the page claiming to
            be working when it is in fact parked on the reader. */}
        <Icon
          size={16}
          weight={waiting ? "regular" : "fill"}
          className="flex-shrink-0"
          style={{ color }}
        />
        <span
          className={`text-[13px] font-medium${waiting ? " tw-breathe" : ""}`}
          style={{ color }}
        >
          {title}
        </span>
      </div>
      {hint ? (
        <div className="text-[11px] text-[var(--text-muted)]">{hint}</div>
      ) : null}
      {action ? <div>{action}</div> : null}
    </div>
  );
}
